import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { getPlayerProfile } from "../lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

const CAT_LABEL = { passing: "Passing", rushing: "Rushing", receiving: "Receiving" };

const StatLine = ({ line }) => {
  const s = line.stats;
  const items = Object.entries(s).map(([k, v]) => ({ k, v }));
  return (
    <div data-testid={`profile-line-${line.category}`} className="border border-border bg-card">
      <div className="px-4 py-2 bg-secondary border-b border-border label-caps">
        {CAT_LABEL[line.category]} · Season
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-border/50">
        {items.map(({ k, v }) => (
          <div key={k} className="px-4 py-3">
            <p className="label-caps">{k}</p>
            <p className="font-display text-3xl text-primary leading-none">{v}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function PlayerProfile() {
  const { id } = useParams();
  const { data, isLoading } = useQuery({ queryKey: ["player", id], queryFn: () => getPlayerProfile(id) });

  if (isLoading) return <p className="text-muted-foreground">Loading player…</p>;
  if (!data) return <p className="text-muted-foreground">Player not found.</p>;

  const { player, lines, log } = data;
  const chart = log.map((l) => ({ label: `W${l.week} ${l.category.slice(0, 3).toUpperCase()}`, yards: l.yards, category: l.category }));

  return (
    <div data-testid="player-profile-page" className="space-y-8">
      <Link to="/stats" data-testid="profile-back" className="label-caps hover:text-primary transition-colors duration-150">
        ← Back to Player Stats
      </Link>

      <section className="flex items-center gap-5 border-b-2 border-border pb-5">
        <img src={player.teamLogo} alt={player.team} className="w-20 h-20 object-contain" />
        <div>
          <p className="label-caps text-primary">{player.position}</p>
          <h1 className="font-display text-5xl sm:text-6xl leading-none">{player.name}</h1>
          <Link to={`/team/${player.team}`} className="text-sm text-muted-foreground hover:text-primary transition-colors duration-150">
            {player.teamName} →
          </Link>
        </div>
      </section>

      <div className="space-y-4">
        {lines.map((l) => (
          <StatLine key={l.category} line={l} />
        ))}
      </div>

      {log.length > 0 && (
        <section>
          <h2 className="font-display text-3xl mb-4 border-b-2 border-border pb-2">GAME LOG</h2>
          <div className="border border-border bg-card p-5 mb-5">
            <p className="label-caps mb-4">Yards by Week (weeks led the league)</p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chart} margin={{ top: 0, right: 10, left: -10, bottom: 0 }}>
                <XAxis dataKey="label" stroke="hsl(0 0% 45%)" tick={{ fontSize: 11 }} />
                <YAxis stroke="hsl(0 0% 45%)" tick={{ fontSize: 11 }} />
                <Tooltip cursor={{ fill: "hsl(0 0% 100% / 0.05)" }}
                  contentStyle={{ background: "hsl(0 0% 8%)", border: "1px solid hsl(75 100% 50%)" }} />
                <Bar dataKey="yards" radius={0}>
                  {chart.map((_, i) => <Cell key={i} fill="hsl(75 100% 50%)" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="border border-border bg-card divide-y divide-border/40">
            {log.map((l, i) => (
              <div key={i} data-testid={`log-row-${i}`} className="flex items-center gap-4 px-4 py-2.5">
                <span className="font-display text-xl w-14 text-muted-foreground">WK {l.week}</span>
                <span className="text-sm font-semibold w-24 uppercase tracking-wider text-primary">{CAT_LABEL[l.category]}</span>
                <span className="font-display text-2xl tabular-nums">{l.yards}<span className="text-sm text-muted-foreground ml-1">YDS</span></span>
                <span className="ml-auto text-xs text-muted-foreground">{l.sub}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
